import { useState } from 'react'
import { useParams } from 'react-router-dom'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { Plus } from '@factorialco/f0-react/icons/app'
import { DocumentsPageDocument } from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import NotFound from 'modules/core/components/NotFound'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import FileSidebar from 'modules/bookkeepers/components/PayrollCycle/FileSidebar'
import FilesCategoryList from 'modules/bookkeepers/components/PayrollCycle/FilesCategoryList'

const Documents = () => {
  const { company } = useCurrentSession()
  const { id } = useParams()
  const payrollRunId = Number(id)
  const [openFileSidebar, setOpenFileSidebar] = useState<boolean>(false)
  const [lastFileCreatedAt, setLastFileCreatedAt] = useState<number | undefined>()
  const { loading, data } = useQuery(DocumentsPageDocument, {
    variables: {
      companyId: company.id,
      payrollRunId,
    },
    skip: !id || isNaN(payrollRunId),
  })

  const sharedCategories =
    data?.compensations.fileCategoriesConnection.nodes.filter(
      (category) => category.categoryType !== 'exported'
    ) ?? []
  const exportedCategory = data?.compensations.fileCategoriesConnection.nodes.find(
    (category) => category.categoryType === 'exported'
  )

  if (!id || isNaN(payrollRunId)) {
    return <NotFound />
  }

  if (loading) return <Loading />

  if (!data?.compensations?._policies?.compensationActivityManage?.can) {
    return <NotFound />
  }

  const emptyStateSharedCategories = {
    'no-data': {
      title: i18n.t('bookkeepers.clients.payroll_cycles.documents.empty_state.title'),
      description: i18n.t(
        'bookkeepers.clients.payroll_cycles.documents.empty_state.description_shared'
      ),
    },
  }

  const emptyStateExportedCategory = {
    'no-data': {
      title: i18n.t('bookkeepers.clients.payroll_cycles.documents.empty_state.title'),
      description: i18n.t(
        'bookkeepers.clients.payroll_cycles.documents.empty_state.description_exported'
      ),
    },
  }

  return (
    <>
      <Box padding="s24" gap="s8">
        <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center">
          <Box flexDirection="column" justifyContent="spaceBetween" gap="s8">
            <Text.Normal color="grey1000" weight="semibold">
              {i18n.t('bookkeepers.clients.payroll_cycles.documents.shared.title')}
            </Text.Normal>
            <Text size="200" color="grey700">
              {i18n.t('bookkeepers.clients.payroll_cycles.documents.shared.description')}
            </Text>
          </Box>

          <Box flexDirection="row" alignItems="flexEnd">
            <F0Button
              onClick={() => setOpenFileSidebar(true)}
              icon={Plus}
              variant="outline"
              label={i18n.t('bookkeepers.clients.payroll_cycles.documents.add_document')}
            />
          </Box>
        </Box>
        <Box alignItems="center">
          {sharedCategories && sharedCategories.length > 0 && (
            <FilesCategoryList
              companyId={company.id}
              payrollRunId={payrollRunId}
              categories={sharedCategories}
              emptyState={emptyStateSharedCategories}
              refetchTrigger={lastFileCreatedAt}
            />
          )}
        </Box>
      </Box>
      <Box
        padding="s24"
        gap="s8"
        border={{ top: { color: 'darker160', width: 's1', style: 'dashed' } }}
      >
        <Text.Normal color="grey1000" weight="semibold">
          {i18n.t('bookkeepers.clients.payroll_cycles.documents.internal.title')}
        </Text.Normal>
        <Text size="200" color="grey700">
          {i18n.t('bookkeepers.clients.payroll_cycles.documents.internal.description')}
        </Text>
        <Box alignItems="center">
          {exportedCategory && (
            <FilesCategoryList
              companyId={company.id}
              payrollRunId={payrollRunId}
              categories={[exportedCategory]}
              emptyState={emptyStateExportedCategory}
              refetchTrigger={lastFileCreatedAt}
            />
          )}
        </Box>
      </Box>
      <FileSidebar
        open={openFileSidebar}
        onClose={() => setOpenFileSidebar(false)}
        payrollRunId={payrollRunId}
        onSuccess={() => setLastFileCreatedAt(Date.now())}
        fileCategories={sharedCategories}
      />
    </>
  )
}

export default Documents
